import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, Facebook, Linkedin, Youtube, MessageCircle, Download } from "lucide-react";
import { useSettings, waLink } from "@/context/AppContext";
import { brochureHref } from "@/components/BrochureButton";

export default function Footer() {
  const { settings } = useSettings();
  const biz = settings?.business || {};
  const year = new Date().getFullYear();
  const social = biz.social || {};

  const socials = [
    { key: "whatsapp", url: waLink(settings), Icon: MessageCircle, label: "WhatsApp", accent: true },
    { key: "instagram", url: social.instagram, Icon: Instagram, label: "Instagram" },
    { key: "facebook", url: social.facebook, Icon: Facebook, label: "Facebook" },
    { key: "linkedin", url: social.linkedin, Icon: Linkedin, label: "LinkedIn" },
    { key: "youtube", url: social.youtube, Icon: Youtube, label: "YouTube" },
  ].filter((s) => s.url);

  return (
    <footer className="relative bg-forest-deep text-parchment grain" data-testid="site-footer">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16 pt-16 pb-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="font-serif text-3xl font-semibold">
              {(biz.brand || "AnVibes™").replace("™", "")}
              <sup className="text-gold text-sm align-super">™</sup>
            </p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.32em] text-gold/90">{biz.public_name || "AnVibes Exports"}</p>
            <p className="mt-4 text-sm text-[#9EB8A8]">{biz.legal_name || "AnVibes Overseas Imports and Exports"}</p>
            <p className="mt-5 flex items-start gap-2.5 text-sm text-[#9EB8A8] leading-relaxed">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-gold" />
              <span data-testid="footer-address">{biz.address}</span>
            </p>
            {socials.length > 0 && (
              <div className="mt-6 flex gap-3" data-testid="footer-social-links">
                {socials.map(({ key, url, Icon, label, accent }) => (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    title={label}
                    data-testid={`social-link-${key}`}
                    className={`flex h-10 w-10 items-center justify-center border transition-colors duration-300 hover:border-gold hover:text-gold ${
                      accent ? "border-gold/50 bg-gold/10 text-gold-light" : "border-white/15 text-parchment/70"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <p className="eyebrow">Explore</p>
            <ul className="mt-5 space-y-2.5 text-sm">
              {[["Home", "/"], ["About", "/about"], ["Products", "/products"], ["Quality", "/quality"], ["Export Markets", "/markets"], ["Packaging", "/packaging"], ["FAQ", "/faq"], ["Contact", "/contact"], ["Request a Quote", "/quote"]].map(([label, to]) => (
                <li key={to + label}>
                  <Link to={to} data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`} className="text-parchment/70 transition-colors hover:text-gold-light">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="eyebrow">Products</p>
            <ul className="mt-5 space-y-2.5 text-sm">
              <li><Link to="/rice" data-testid="footer-link-rice" className="text-parchment/70 hover:text-gold-light transition-colors">Indian Rice</Link></li>
              <li><Link to="/spices" data-testid="footer-link-spices" className="text-parchment/70 hover:text-gold-light transition-colors">Indian Spices</Link></li>
              <li><Link to="/tea" data-testid="footer-link-tea" className="text-parchment/70 hover:text-gold-light transition-colors">Indian Tea</Link></li>
              <li><Link to="/products/basmati-rice" className="text-parchment/70 hover:text-gold-light transition-colors">Basmati Rice</Link></li>
              <li><Link to="/products/black-pepper" className="text-parchment/70 hover:text-gold-light transition-colors">Black Pepper</Link></li>
              <li><Link to="/products/green-tea" className="text-parchment/70 hover:text-gold-light transition-colors">Green Tea</Link></li>
              {brochureHref(settings) && (
                <li>
                  <a href={brochureHref(settings)} target="_blank" rel="noopener noreferrer" data-testid="footer-brochure-link" className="inline-flex items-center gap-1.5 text-gold-light hover:text-gold transition-colors">
                    <Download className="h-3.5 w-3.5" /> {settings.brochure.title || "Product Catalogue"} (PDF)
                  </a>
                </li>
              )}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="eyebrow">Contact</p>
            <ul className="mt-5 space-y-3 text-sm">
              {(biz.phones || []).map((p) => (
                <li key={p.tel}>
                  <a href={`tel:${p.tel}`} data-testid={`footer-phone-${p.tel}`} className="flex items-center gap-2.5 text-parchment/70 hover:text-gold-light transition-colors">
                    <Phone className="h-4 w-4 text-gold" /> {p.display}
                  </a>
                </li>
              ))}
              <li>
                <a href={`mailto:${biz.email || "anand@anvibesexports.com"}`} data-testid="footer-email" className="flex items-center gap-2.5 text-parchment/70 hover:text-gold-light transition-colors">
                  <Mail className="h-4 w-4 text-gold" /> {biz.email || "anand@anvibesexports.com"}
                </a>
              </li>
            </ul>
            <p className="eyebrow mt-8">Legal</p>
            <ul className="mt-4 space-y-2.5 text-sm">
              {[["Privacy Policy", "/legal/privacy-policy"], ["Terms & Conditions", "/legal/terms-and-conditions"], ["Cookie Policy", "/legal/cookie-policy"], ["Disclaimer", "/legal/disclaimer"], ["Shipping / Export Info", "/legal/shipping-export-information"]].map(([label, to]) => (
                <li key={to}>
                  <Link to={to} data-testid={`footer-legal-${to.split("/").pop()}`} className="text-parchment/60 hover:text-gold-light transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 border-t border-white/10 pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-xs text-parchment/50" data-testid="footer-copyright">
            © {year} {biz.public_name || "AnVibes Exports"}. All Rights Reserved.
          </p>
          {(biz.iec || biz.gstin) && (
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-parchment/40" data-testid="footer-registration">
              {biz.iec && <span>IEC {biz.iec}</span>}
              {biz.iec && biz.gstin && <span className="mx-2 text-gold/50">·</span>}
              {biz.gstin && <span>GSTIN {biz.gstin}</span>}
            </p>
          )}
        </div>
      </div>
    </footer>
  );
}
